/**
 * The sticky save bar of the builder editors.
 *
 * Composed from design-system primitives. The design system's `EditorActionBar`
 * has one save action plus cancel and a destructive action, so it cannot carry
 * Save draft and Publish together (and Gold Master's design system does not
 * export it yet). This bar keeps that component's behaviour:
 *  1. sticky at the bottom of the editor on desktop and tablet; on a phone (640px and below) it sits in the
 *     page flow after the builder, so it never covers the canvas;
 *  2. an unsaved-changes indicator, announced to assistive technology;
 *  3. the acted button shows a spinner and `aria-busy`, and every action is
 *     disabled while one is in flight (no duplicate submissions);
 *  4. a refused write is shown in the bar, the author's input stays;
 *  5. a completed write is confirmed in place ("Saved", "Published");
 *  6. buttons with nothing to act on are disabled with the reason in the note;
 *  7. a row whose content can't be edited shows no content commits at all, only
 *     Version history (and Discard draft, which the server decides).
 * Order: Version history leads on the left; on the right Discard draft, Discard
 * changes (only while there are unsaved changes), Save draft, then Publish, the
 * primary action, last. Every button is the 36px page-action size.
 */
import type { ReactNode } from "react";
import { Btn, Icon, IconButton, Spinner } from "@v0y4ge/jrni-ui";
import type { Tr } from "../model";

export type BarBusy = "save" | "publish" | "discard" | "discardDraft" | null;

export interface BuilderActionBarProps {
  t: Tr;
  dirty: boolean;
  busy: BarBusy;
  readOnly: boolean;
  /** False when this row's content can't be edited here (permission or the row itself): no Discard changes, Save draft
   *  or Publish is shown. Discard draft and Version history keep their own conditions. Default true. */
  canEditContent?: boolean;
  /** A saved draft exists on the server (for "Discard draft"). */
  hasServerDraft: boolean;
  /** Why Publish can't run now, or null. */
  publishBlocked: string | null;
  /** Why Save and Publish can't run now, or null. The author's input stays. Discard draft is never
   *  pre-disabled by this: whether a draft can be discarded is the server's answer, shown as a refusal. */
  blocked?: string | null;
  /** Shown left of the buttons: what Publish will do, or why something is disabled. */
  note: ReactNode;
  /** A completed action, announced once ("Draft saved."). */
  confirmation: string | null;
  /** The refusal of the last action, if any (rendered above the buttons). */
  failure: ReactNode;
  onSave: () => void;
  onDiscardChanges: () => void;
  onDiscardDraft: (() => void) | null;
  onPublish: () => void;
  onHistory: (() => void) | null;
  testid: string;
}

export function BuilderActionBar(p: BuilderActionBarProps) {
  const { t } = p;
  const inFlight = p.busy !== null;
  const writeBlocked = !!p.blocked;
  const content = !p.readOnly && p.canEditContent !== false;
  const spin = (kind: BarBusy) => (p.busy === kind ? <Spinner size="sm" label={t("messaging.builder.working", "Working")} /> : undefined);
  const historyLabel = t("messaging.builder.history", "Version history");
  return (
    <div className="msgb-footer" data-testid={p.testid}>
      {p.failure}
      <div className="msgb-footrow">
        <div className="msgb-footlead">
          {p.onHistory ? (
            <>
              <span className="msgb-hide-phone"><Btn variant="ghost" size="md" icon={<Icon.clock size={14} />} onClick={p.onHistory} disabled={inFlight}>{historyLabel}</Btn></span>
              <span className="msgb-show-phone"><IconButton size={36} variant="ghost" label={historyLabel} onClick={() => p.onHistory && p.onHistory()} disabled={inFlight}><Icon.clock size={16} /></IconButton></span>
            </>
          ) : null}
          {p.dirty ? <span className="msgb-dirty" role="status">{t("messaging.builder.unsaved", "Unsaved changes")}</span> : null}
          {p.confirmation && !p.dirty ? <span className="msgb-dirty" role="status" aria-live="polite" style={{ color: "var(--jrni-color-semantic-green-1)" }}>{p.confirmation}</span> : null}
          {/* On a phone the general note folds away; a blocking reason always shows. */}
          <p className="msgb-pubnote" data-reason={p.blocked || p.publishBlocked || p.canEditContent === false ? "1" : undefined} title={typeof p.note === "string" ? p.note : undefined}>{p.note}</p>
        </div>
        <div className="msgb-footend">
          {!p.readOnly && p.onDiscardDraft && p.hasServerDraft && !p.dirty ? (
            <Btn variant="ghost" size="md" onClick={p.onDiscardDraft} disabled={inFlight} aria-busy={p.busy === "discardDraft"} icon={spin("discardDraft")} testid={`${p.testid}-discard-draft`}>
              {t("messaging.builder.discardDraft", "Discard draft")}
            </Btn>
          ) : null}
          {content && p.dirty ? (
            <Btn variant="ghost" size="md" onClick={p.onDiscardChanges} disabled={inFlight} testid={`${p.testid}-discard`}>
              {t("messaging.builder.discardChanges", "Discard changes")}
            </Btn>
          ) : null}
          {content ? (
            <Btn variant="secondary" size="md" onClick={p.onSave} disabled={!p.dirty || inFlight || writeBlocked} title={p.blocked || undefined} aria-busy={p.busy === "save"} icon={spin("save")} testid={`${p.testid}-save`}>
              {p.busy === "save" ? t("messaging.builder.saving", "Saving…") : t("messaging.builder.saveDraft", "Save draft")}
            </Btn>
          ) : null}
          {content ? (
            <Btn variant="primary" size="md" onClick={p.onPublish} disabled={inFlight || writeBlocked || !!p.publishBlocked || (!p.dirty && !p.hasServerDraft)}
              aria-busy={p.busy === "publish"} icon={spin("publish") || <Icon.send size={14} />} title={p.blocked || p.publishBlocked || undefined} testid={`${p.testid}-publish`}>
              {p.busy === "publish" ? t("messaging.builder.publishing", "Publishing…") : t("messaging.builder.publish", "Publish")}
            </Btn>
          ) : null}
        </div>
      </div>
    </div>
  );
}
